import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';

import Sidebar from './Sidebar';
import Topbar from './Topbar';
import Breadcrumbs from './Breadcrumbs';
import { getShellMuiTheme } from './muiTheme';
import { useThemeMode } from './ThemeMode';
import './tokens.css';

const COLLAPSE_KEY = 'shell.sidebar.collapsed';

/** Страницы, занимающие всю высоту (без breadcrumbs и внутренних паддингов). */
const FULL_HEIGHT_PATHS = ['/', '/chat', '/technical-task'];

type AppShellProps = { children: ReactNode };

const AppShellInner = ({ children }: AppShellProps) => {
  const { pathname } = useLocation();
  const { mode } = useThemeMode();
  const muiTheme = useMemo(() => getShellMuiTheme(mode), [mode]);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  const isFullHeight = FULL_HEIGHT_PATHS.includes(pathname);

  return (
    <ThemeProvider theme={muiTheme}>
      <div
        style={{
          display: 'flex',
          height: '100vh',
          width: '100vw',
          background: 'var(--shell-bg-canvas)',
          color: 'var(--shell-text-primary)',
          fontFamily:
            "'Inter', 'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          overflow: 'hidden',
        }}
      >
        <Sidebar collapsed={collapsed} onToggleCollapsed={() => setCollapsed((v) => !v)} />

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Topbar />
          {!isFullHeight && <Breadcrumbs />}

          <main
            style={{
              flex: 1,
              minHeight: 0,
              overflow: isFullHeight ? 'hidden' : 'auto',
              display: isFullHeight ? 'flex' : 'block',
              flexDirection: isFullHeight ? 'column' : undefined,
              padding: isFullHeight ? 0 : 20,
              background: 'var(--shell-bg-canvas)',
            }}
          >
            {children}
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
};

const AppShell = ({ children }: AppShellProps) => {
  return <AppShellInner>{children}</AppShellInner>;
};

export default AppShell;
